import React, { useState, useEffect, useCallback } from "react";
import { auth, db, storage, functions } from "../firebase"; // Import functions
import { doc, getDoc, updateDoc, setDoc, collection, getDocs, query } from "firebase/firestore";
import { ref, listAll, getDownloadURL } from "firebase/storage";
import { onAuthStateChanged } from "firebase/auth";
import { httpsCallable } from "firebase/functions"; // Import for Cloud Functions
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  CssBaseline,
  useMediaQuery,
  useTheme,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
  Container,
  Paper,
  TextField,
  Button,
  Avatar,
  Grid2 as Grid,
  Chip,
  Divider,
  Snackbar,
  Alert,
  Card,
  CardContent,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fade,
} from "@mui/material";
import { styled } from "@mui/system";
import { useNavigate, useLocation } from "react-router-dom";
import CryptoJS from "crypto-js";
import PersonIcon from "@mui/icons-material/Person";
import BarChartIcon from "@mui/icons-material/BarChart";
import PaymentIcon from "@mui/icons-material/Payment";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import UpgradeIcon from "@mui/icons-material/Upgrade";
import StarIcon from "@mui/icons-material/Star";
import DownloadIcon from "@mui/icons-material/Download";
import MathGemLogoMain from "../assets/MathGemLogoMain.png";

// CyberSource configuration
const CYBERSOURCE_CONFIG = {
  ACCESS_KEY: '0105d945f85c368cbec7459cc6d30465',
  PROFILE_ID: '14098329-8313-4F96-909A-3C957A276641',
  SECRET_KEY: '2052d69cb9794c9991ed44a1efa528a394f84c9653ec4077af586faa177591fd71c7a66e055a4371bc163c6e80ebd36f8339a7f784f7478eac4dc810b0531459f751b7399aa84a649c9430b0f5e89c2f86dd0fa12299406fbadd2e9e7b8823d7f0ada056879a459eaf110d019f69e36a1754fc747bc042b7b4122f08f390624b',
  URL: 'https://secureacceptance.cybersource.com/pay',
};

const colors = {
  primary: '#2ecc71',
  secondary: '#34495e',
  background: '#f5f7fa',
  accent: '#3498c',
  gradientStart: 'rgba(62,180,137,1)',
  gradientEnd: 'rgba(144,238,144,1)',
};

// Styled components (unchanged)
const SidebarDrawer = styled(Drawer)(({ theme }) => ({
  width: 240,
  flexShrink: 0,
  "& .MuiDrawer-paper": {
    width: 240,
    boxSizing: "border-box",
    background: "linear-gradient(180deg, #2ecc71 0%, #66bb6a 100%)",
    color: "#fff",
    borderRight: "none",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)",
  },
}));

const SidebarLogo = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  textAlign: "center",
  borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
}));

const SidebarListItem = styled(ListItem)(({ selected }) => ({
  borderRadius: "0 25px 25px 0",
  margin: "8px 16px",
  padding: "12px 24px",
  background: selected ? "rgba(255, 255, 255, 0.2)" : "transparent",
  "&:hover": {
    background: "rgba(255, 255, 255, 0.3)",
    transform: "translateX(5px)",
  },
  transition: "all 0.3s ease",
  "& .MuiListItemIcon-root": {
    color: "#fff",
    minWidth: "40px",
  },
  "& .MuiListItemText-primary": {
    fontWeight: 600,
  },
}));

const MainContent = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  marginLeft: { md: 240 },
  background: "#f5f7fa",
  minHeight: "100vh",
}));

const ContentPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: "20px",
  background: "#ffffff",
  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.05)",
  border: "1px solid rgba(0, 0, 0, 0.05)",
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  background: "linear-gradient(90deg, #2ecc71 0%, #66bb6a 100%)",
  padding: theme.spacing(3),
  borderRadius: "20px 20px 0 0",
  color: "#fff",
  textAlign: "center",
  margin: "-32px -32px 32px -32px",
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: "12px",
  padding: theme.spacing(1.5, 4),
  textTransform: "none",
  fontWeight: 700,
  fontSize: "1rem",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-3px)",
    boxShadow: "0 6px 20px rgba(0, 0, 0, 0.15)",
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#fafafa",
    transition: "all 0.3s ease",
    "&:hover fieldset": {
      borderColor: "#2ecc71",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#2ecc71",
    },
  },
  "& .MuiInputBase-input": {
    padding: theme.spacing(1.5),
    fontSize: "1rem",
  },
  "& .MuiInputLabel-root": {
    color: "#34495e",
    fontWeight: 500,
  },
}));

const StatCard = styled(Card)(({ theme }) => ({
  borderRadius: "16px",
  background: "linear-gradient(135deg, #f5f7fa 0%, #e8ecef 100%)",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
  padding: theme.spacing(2),
  textAlign: "center",
  transition: "transform 0.3s ease",
  "&:hover": {
    transform: "translateY(-5px)",
  },
}));

const PlanCard = styled(Card)(({ theme }) => ({
  borderRadius: "16px",
  background: "linear-gradient(135deg, #f5f7fa 0%, #e8ecef 100%)",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
  padding: theme.spacing(2),
  textAlign: "center",
  transition: "transform 0.3s ease",
  "&:hover": {
    transform: "translateY(-5px)",
  },
}));

const SelectButton = styled(Button)(({ theme }) => ({
  borderRadius: "12px",
  padding: theme.spacing(1.5, 4),
  textTransform: "none",
  fontWeight: 700,
  fontSize: "1rem",
  background: "linear-gradient(90deg, #2ecc71, #66bb6a)",
  color: "#fff",
  "&:hover": {
    background: "linear-gradient(90deg, #27ae60, #5cb85c)",
    transform: "translateY(-3px)",
    boxShadow: "0 6px 20px rgba(0, 0, 0, 0.15)",
  },
}));

const ModernDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: 16,
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
    background: "linear-gradient(135deg, #ffffff 0%, #f0f4f8 100%)",
    border: "1px solid rgba(173, 223, 173, 0.3)",
    maxWidth: 500,
  },
}));

const ModernTable = styled(TableContainer)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
  "& .MuiTableRow-root:nth-of-type(odd)": {
    backgroundColor: "#f9fafb",
  },
  "& .MuiTableRow-root:hover": {
    backgroundColor: "#e8f5e9",
  },
}));

const ProfileDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState({
    fullName: "",
    email: "",
    gradeLevel: "",
    learningGoal: "",
    currentTarget: "",
    status: "trial",
    achievements: 0,
    role: "",
  });
  const [originalUserData, setOriginalUserData] = useState(null);
  const [tempData, setTempData] = useState({
    fullName: "",
    gradeLevel: "",
    learningGoal: "",
    currentTarget: "",
  });
  const [stats, setStats] = useState({
    viewedTopicsPercentage: 0,
    achievements: 0,
  });
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [fullNameError, setFullNameError] = useState("");
  const navigate = useNavigate();

  const navItems = [
    { label: "Profile", icon: <PersonIcon /> },
    { label: "Statistics", icon: <BarChartIcon /> },
    { label: "Subscriptions", icon: <PaymentIcon /> },
  ];

  const PLANS = [
    { name: "Monthly", price: 300, display: "Rs300 / month", amount: "300", period: "monthly" },
    { name: "Yearly", price: 3000, display: "Rs3000 / year", amount: "3000", period: "yearly" },
    ...(userData.role === "admin"
      ? [{ name: "Test", price: 1, display: "Rs1 / test", amount: "1", period: "test" }]
      : []),
  ];

  useEffect(() => {
    const fetchData = async (user) => {
      if (user) {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        const topicsCollection = collection(db, "topics");
        const topicsSnapshot = await getDocs(query(topicsCollection));
        const totalTopics = topicsSnapshot.docs.length;
        const viewedTopicsSnap = await getDocs(collection(db, `users/${user.uid}/viewedResources`));
        const viewedTopicsCount = viewedTopicsSnap.docs.length;
        const percentage = totalTopics > 0 ? (viewedTopicsCount / totalTopics) * 100 : 0;
        const paymentsCollection = collection(db, `users/${user.uid}/payments`);
        const paymentSnapshot = await getDocs(paymentsCollection);
        const paymentList = paymentSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        try {
          const invoicesRef = ref(storage, `invoices/${user.uid}`);
          const invoiceList = await listAll(invoicesRef);
          const invoiceData = await Promise.all(
            invoiceList.items.map(async (item) => {
              const url = await getDownloadURL(item);
              return {
                name: item.name,
                url,
                invoiceId: item.name.split('.')[0],
                date: paymentList.find(p => p.invoiceId === item.name.split('.')[0])?.date || new Date().toISOString(),
              };
            })
          );
          setInvoices(invoiceData);
        } catch (error) {
          console.error("Error fetching invoices:", error);
          setSnackbar({
            open: true,
            message: "Failed to load invoices. Please try again.",
            severity: "error",
          });
        }

        if (userSnap.exists()) {
          const data = {
            fullName: userSnap.data().fullName || "",
            email: user.email || "",
            gradeLevel: userSnap.data().gradeLevel || "",
            learningGoal: userSnap.data().learningGoal || "",
            currentTarget: userSnap.data().currentTarget || "",
            status: userSnap.data().status || "trial",
            achievements: userSnap.data().achievements || 0,
            role: userSnap.data().role || "",
          };
          setUserData(data);
          setOriginalUserData(data);
          setTempData({
            fullName: data.fullName,
            gradeLevel: data.gradeLevel,
            learningGoal: data.learningGoal,
            currentTarget: data.currentTarget,
          });
        }
        setStats({
          viewedTopicsPercentage: percentage.toFixed(1),
          achievements: userSnap.exists() ? userSnap.data().achievements || 0 : 0,
        });
        setPayments(paymentList);
        setLoading(false);
      }
    };

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        fetchData(currentUser);
      } else {
        navigate("/sign-in");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleNavClick = (index) => {
    setActiveTab(index);
  };

  const validateFullName = (value) => {
    if (!value.trim()) {
      return "Full name is required";
    }
    if (value.trim().length < 2) {
      return "Full name must be at least 2 characters";
    }
    if (!/^[a-zA-Z\s-']+$/.test(value.trim())) {
      return "Full name can only contain letters, spaces, hyphens, or apostrophes";
    }
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTempData((prev) => ({ ...prev, [name]: value }));
    if (name === "fullName") {
      setFullNameError(validateFullName(value));
    }
  };

  const handleEditToggle = () => {
    if (isEditing) {
      setTempData({
        fullName: userData.fullName,
        gradeLevel: userData.gradeLevel,
        learningGoal: userData.learningGoal,
        currentTarget: userData.currentTarget,
      });
      setFullNameError("");
    }
    setIsEditing(!isEditing);
  };

  const handleSave = async () => {
    if (!user) return;

    const nameError = validateFullName(tempData.fullName);
    if (nameError) {
      setFullNameError(nameError);
      return;
    }

    setLoading(true);
    try {
      const userRef = doc(db, "users", user.uid);
      const updates = {};
      if (tempData.fullName.trim() !== originalUserData.fullName) {
        updates.fullName = tempData.fullName.trim();
      }
      if (tempData.gradeLevel !== originalUserData.gradeLevel) {
        updates.gradeLevel = tempData.gradeLevel;
      }
      if (tempData.learningGoal !== originalUserData.learningGoal) {
        updates.learningGoal = tempData.learningGoal;
      }
      if (tempData.currentTarget !== originalUserData.currentTarget) {
        updates.currentTarget = tempData.currentTarget;
      }

      await updateDoc(userRef, updates);
      const newUserData = {
        ...userData,
        fullName: tempData.fullName.trim(),
        gradeLevel: tempData.gradeLevel,
        learningGoal: tempData.learningGoal,
        currentTarget: tempData.currentTarget,
      };
      setUserData(newUserData);
      setOriginalUserData(newUserData);
      setIsEditing(false);
      setFullNameError("");
      setSnackbar({ open: true, message: "Profile updated successfully!", severity: "success" });
    } catch (error) {
      console.error("Error updating profile:", error);
      let errorMessage = "Error updating profile";
      if (error.code === "permission-denied") {
        errorMessage = "You don't have permission to update this profile";
      } else if (error.code === "unavailable") {
        errorMessage = "Network error, please try again later";
      }
      setSnackbar({ open: true, message: errorMessage, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setOpenDialog(true);
  };

  const generatePaymentData = useCallback((plan) => {
    const transactionUuid = Math.random().toString(36).substring(2, 15);
    const signedDateTime = new Date().toISOString().replace(/\.[0-9]{3}/, '');
    const signedFieldNames =
      'access_key,profile_id,transaction_uuid,signed_date_time,transaction_type,reference_number,amount,currency,locale,signed_field_names';

    const data = {
      access_key: CYBERSOURCE_CONFIG.ACCESS_KEY,
      profile_id: CYBERSOURCE_CONFIG.PROFILE_ID,
      transaction_uuid: transactionUuid,
      signed_date_time: signedDateTime,
      transaction_type: 'sale',
      reference_number: `ORDER_${Date.now()}_${plan.period}`,
      amount: plan.amount,
      currency: 'SCR',
      locale: 'en',
      signed_field_names: signedFieldNames,
      signature: '',
    };

    const dataString = Object.keys(data)
      .filter((key) => key !== 'signature')
      .map((key) => `${key}=${data[key]}`)
      .join(',');
    data.signature = CryptoJS.HmacSHA256(dataString, CYBERSOURCE_CONFIG.SECRET_KEY).toString(
      CryptoJS.enc.Base64
    );

    return data;
  }, []);

  const handleConfirmPlan = async () => {
    if (!user || !selectedPlan) {
      setSnackbar({ open: true, message: "Please select a plan.", severity: "error" });
      return;
    }

    const plan = PLANS.find((p) => p.period === selectedPlan.period);
    if (!plan) {
      setSnackbar({ open: true, message: "Invalid plan selected.", severity: "error" });
      return;
    }

    setIsProcessing(true);
    setSnackbar({ open: false, message: "", severity: "success" });

    try {
      const formData = generatePaymentData(plan);
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = CYBERSOURCE_CONFIG.URL;

      Object.entries(formData).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value;
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      console.error("Payment initiation error:", err);
      setSnackbar({
        open: true,
        message: "Failed to initiate payment. Please try again.",
        severity: "error",
      });
      setIsProcessing(false);
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedPlan(null);
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <div style={{ background: '#f5f7fa' }}>
        <Container
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            background: colors.background,
          }}
        >
          <img src={MathGemLogoMain} width={350} alt="Math Gem Logo" />
          <Fade in={true} timeout={600}>
            <LinearProgress
              sx={{ color: colors.primary, mb: 2, mt: 5, width: '50%' }}
              size={60}
              thickness={4}
            />
          </Fade>
          <Typography variant="h6" sx={{ color: colors.secondary }}>
            Loading your Profile...
          </Typography>
        </Container>
      </div>
    );
  }

  const ProfileSection = () => (
    <ContentPaper elevation={8}>
      <HeaderBox>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {userData.fullName || "User Profile"}
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9, mt: 1 }}>
          Account: <b>{userData.status}</b>
        </Typography>
      </HeaderBox>
      <Box sx={{ p: 2 }}>
        <Divider sx={{ mb: 4 }} />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <StyledTextField
            fullWidth
            label="Full Name"
            name="fullName"
            value={tempData.fullName}
            onChange={handleChange}
            variant="outlined"
            error={!!fullNameError}
            helperText={fullNameError}
            disabled={!isEditing}
          />
          <StyledTextField
            fullWidth
            label="Email"
            name="email"
            value={userData.email}
            disabled
            variant="outlined"
            InputProps={{ readOnly: true }}
          />
          <StyledTextField
            fullWidth
            label="Grade Level"
            name="gradeLevel"
            value={tempData.gradeLevel}
            onChange={handleChange}
            disabled={!isEditing}
            variant="outlined"
            InputProps={{ readOnly: !isEditing }}
          />
          <StyledTextField
            fullWidth
            label="Learning Goals"
            name="learningGoal"
            value={tempData.learningGoal}
            onChange={handleChange}
            disabled={!isEditing}
            variant="outlined"
            multiline
            rows={4}
            InputProps={{ readOnly: !isEditing }}
          />
          <StyledTextField
            fullWidth
            label="Current Target"
            name="currentTarget"
            value={tempData.currentTarget}
            onChange={handleChange}
            disabled={!isEditing}
            variant="outlined"
            multiline
            rows={4}
            InputProps={{ readOnly: !isEditing }}
          />
          <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 4, flexWrap: "wrap" }}>
            {isEditing ? (
              <>
                <StyledButton
                  variant="contained"
                  color="success"
                  onClick={handleSave}
                  disabled={loading || !!fullNameError}
                  startIcon={<SaveIcon />}
                  sx={{ background: "linear-gradient(90deg, #2ecc71, #66bb6a)" }}
                >
                  Save Changes
                </StyledButton>
                <StyledButton
                  variant="outlined"
                  color="secondary"
                  onClick={handleEditToggle}
                  disabled={loading}
                  startIcon={<CancelIcon />}
                  sx={{ borderColor: "#34495e", color: "#34495e" }}
                >
                  Cancel
                </StyledButton>
              </>
            ) : (
              <>
                <StyledButton
                  variant="contained"
                  color="primary"
                  onClick={handleEditToggle}
                  startIcon={<EditIcon />}
                  sx={{ background: "linear-gradient(90deg, #3498db, #42a5f5)" }}
                >
                  Edit Profile
                </StyledButton>
                {userData.status !== "premium" && (
                  <StyledButton
                    variant="contained"
                    color="warning"
                    onClick={() => setActiveTab(2)}
                    startIcon={<UpgradeIcon />}
                    sx={{ background: "linear-gradient(90deg, #f39c12, #f1c40f)" }}
                  >
                    Upgrade to Premium
                  </StyledButton>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </ContentPaper>
  );

  const StatisticsSection = () => (
    <ContentPaper elevation={8}>
      <HeaderBox>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Your Learning Statistics
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9, mt: 1 }}>
          Track your progress and achievements
        </Typography>
      </HeaderBox>
      <Box sx={{ p: 2 }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <StatCard>
              <CardContent>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Topics Viewed
                </Typography>
                <Typography variant="h4" color="#2ecc71" sx={{ fontWeight: 700 }}>
                  {stats.viewedTopicsPercentage}%
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={parseFloat(stats.viewedTopicsPercentage)}
                  sx={{
                    mt: 1,
                    height: 8,
                    borderRadius: 4,
                    bgcolor: "#e0e0e0",
                    "& .MuiLinearProgress-bar": { bgcolor: "#2ecc71" },
                  }}
                />
              </CardContent>
            </StatCard>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <StatCard>
              <CardContent>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Achievements
                </Typography>
                <Typography variant="h4" color="#3498db" sx={{ fontWeight: 700 }}>
                  {stats.achievements}
                </Typography>
                <StarIcon sx={{ fontSize: 32, color: "#f1c40f", mt: 1 }} />
              </CardContent>
            </StatCard>
          </Grid>
        </Grid>
      </Box>
    </ContentPaper>
  );

  const SubscriptionsSection = () => (
    <ContentPaper elevation={8}>
      <HeaderBox>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Your Subscription
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9, mt: 1 }}>
          Manage your plan and view past invoices
        </Typography>
      </HeaderBox>
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: "#2e7d32" }}>
          Account Status
        </Typography>
        <Chip
          label={userData.status === "premium" ? "Premium" : userData.status === "pending" ? "Pending" : "Trial"}
          color={userData.status === "premium" ? "success" : userData.status === "pending" ? "warning" : "default"}
          sx={{ fontSize: "1.1rem", fontWeight: 600, py: 2.5, px: 1, mb: 4 }}
        />
        {userData.status !== "premium" && (
          <>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: "#2e7d32" }}>
              Upgrade Your Plan
            </Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {PLANS.map((plan) => (
                <Grid key={plan.name} size={{ xs: 12, sm: 6 }}>
                  <PlanCard>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: "#2e7d32" }}>
                        {plan.name} Plan
                      </Typography>
                      <Typography variant="h5" color="text.secondary" sx={{ my: 2 }}>
                        {plan.display}
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 3, color: "#616161" }}>
                        {plan.name === "Monthly"
                          ? "Access premium features for 30 days."
                          : plan.name === "Yearly"
                          ? "Full-year access and save!"
                          : "Test plan for administrators."}
                      </Typography>
                      <SelectButton onClick={() => handleSelectPlan(plan)}>
                        Select Plan
                      </SelectButton>
                    </CardContent>
                  </PlanCard>
                </Grid>
              ))}
            </Grid>
          </>
        )}
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: "#2e7d32" }}>
          Past Invoices
        </Typography>
        {invoices.length === 0 ? (
          <Typography variant="body1" color="text.secondary">
            No invoices found.
          </Typography>
        ) : (
          <ModernTable>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Invoice ID</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Plan</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map((invoice) => {
                  const payment = payments.find(p => p.invoiceId === invoice.invoiceId) || {};
                  return (
                    <TableRow key={invoice.invoiceId}>
                      <TableCell>{invoice.invoiceId}</TableCell>
                      <TableCell>{new Date(invoice.date).toLocaleDateString()}</TableCell>
                      <TableCell>{payment.plan || 'N/A'}</TableCell>
                      <TableCell>Rs{payment.amount || 'N/A'}</TableCell>
                      <TableCell>{payment.status || 'N/A'}</TableCell>
                      <TableCell>
                        <Button
                          variant="text"
                          color="primary"
                          href={invoice.url}
                          download
                          startIcon={<DownloadIcon />}
                          sx={{ textTransform: "none" }}
                        >
                          Download
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </ModernTable>
        )}
      </Box>
      <ModernDialog open={openDialog} onClose={handleDialogClose}>
        <DialogTitle sx={{ background: "#2ecc71", color: "#fff", fontWeight: 700 }}>
          Confirm Subscription
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Typography sx={{ mb: 2 }}>
            You have selected the {selectedPlan?.name} plan ({selectedPlan?.display}).
            You will be redirected to a secure payment page to complete your subscription.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={handleDialogClose}
            disabled={isProcessing}
            sx={{ textTransform: "none", color: "#616161" }}
          >
            Cancel
          </Button>
          <SelectButton
            onClick={handleConfirmPlan}
            disabled={isProcessing}
            startIcon={isProcessing ? <CircularProgress size={20} /> : null}
          >
            Proceed to Payment
          </SelectButton>
        </DialogActions>
      </ModernDialog>
    </ContentPaper>
  );

  const ResponsePage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const decision = queryParams.get('decision') || 'unknown';
    const transactionId = queryParams.get('transaction_id') || 'N/A';
    const referenceNumber = queryParams.get('req_reference_number') || 'N/A';
    const reasonCode = queryParams.get('reason_code') || 'unknown';
    const message = queryParams.get('message') || 'Unknown error';

    const [modal, setModal] = useState({
      visible: true,
      message: decision === 'ACCEPT' ? 'Payment Successful! Generating invoice...' : `Payment Failed: ${message}`,
      isSuccess: decision === 'ACCEPT',
    });

    useEffect(() => {
      if (decision === 'ACCEPT' && auth.currentUser) {
        const updateFirestore = async () => {
          try {
            const userId = auth.currentUser.uid;
            const paymentId = `PAY-${Date.now()}`;
            const invoiceId = `INV-${Date.now()}`;
            const paymentDate = new Date().toISOString();
            const plan = referenceNumber.includes('yearly') ? 'yearly' : referenceNumber.includes('test') ? 'test' : 'monthly';
            const amount = plan === 'yearly' ? 3000 : plan === 'test' ? 1 : 300;
            const subscriptionEnd = new Date();
            subscriptionEnd.setDate(subscriptionEnd.getDate() + (plan === 'yearly' ? 365 : 30));

            // Update Firestore with user subscription details
            await setDoc(
              doc(db, "users", userId),
              {
                status: 'premium',
                plan,
                subscriptionStart: paymentDate,
                subscriptionEnd: subscriptionEnd.toISOString(),
              },
              { merge: true }
            );

            // Store payment details
            await setDoc(doc(db, `users/${userId}/payments`, paymentId), {
              paymentId,
              userId,
              amount,
              plan,
              date: paymentDate,
              status: 'active',
              invoiceId,
              transactionId,
            });

            // Update local state
            setUserData((prev) => ({ ...prev, status: 'premium', plan }));
            setPayments((prev) => [
              ...prev,
              {
                paymentId,
                amount,
                plan,
                date: paymentDate,
                status: 'active',
                invoiceId,
                transactionId,
              },
            ]);

            // Call Cloud Function to generate and email invoice
            const generateAndEmailInvoice = httpsCallable(functions, 'generateAndEmailInvoice');
            const invoiceData = {
              userId,
              userEmail: user.email,
              fullName: userData.fullName || 'User',
              paymentId,
              invoiceId,
              amount,
              plan,
              date: paymentDate,
              transactionId,
            };

            try {
              const result = await generateAndEmailInvoice(invoiceData);
              if (result.data.success) {
                setModal((prev) => ({
                  ...prev,
                  message: 'Payment Successful! Invoice sent to your email.',
                }));
                // Update invoices state with the new invoice
                const invoiceUrl = result.data.invoiceUrl;
                setInvoices((prev) => [
                  ...prev,
                  {
                    name: `${invoiceId}.pdf`,
                    url: invoiceUrl,
                    invoiceId,
                    date: paymentDate,
                  },
                ]);
              } else {
                throw new Error(result.data.error || 'Failed to generate invoice');
              }
            } catch (error) {
              console.error("Error generating invoice:", error);
              setModal((prev) => ({
                ...prev,
                message: 'Payment successful, but failed to generate invoice. Contact support.',
                isSuccess: false,
              }));
            }
          } catch (err) {
            console.error("Error updating Firestore:", err);
            setModal({
              visible: true,
              message: "Payment successful, but failed to update subscription. Contact support.",
              isSuccess: false,
            });
          }
        };
        updateFirestore();
      }
    }, [decision, transactionId, referenceNumber, user, userData.fullName]);

    const closeModal = () => {
      setModal({ ...modal, visible: false });
      navigate('/dashboard');
    };

    return (
      <Container sx={{ mt: 10, mb: 10, textAlign: 'center' }}>
        <ModernDialog open={modal.visible} onClose={closeModal}>
          <DialogTitle sx={{ background: "#2ecc71", color: "#fff", fontWeight: 700 }}>
            {modal.isSuccess ? 'Success' : 'Error'}
          </DialogTitle>
          <DialogContent sx={{ p: 4 }}>
            <Typography sx={{ mb: 2 }}>{modal.message}</Typography>
            {modal.isSuccess ? (
              <>
                <Typography sx={{ mb: 1 }}>Transaction ID: {transactionId}</Typography>
                <Typography>Reference Number: {referenceNumber}</Typography>
              </>
            ) : (
              <Typography>Reason Code: {reasonCode}</Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <SelectButton onClick={closeModal}>
              {modal.isSuccess ? 'Back to Dashboard' : 'Close'}
            </SelectButton>
          </DialogActions>
        </ModernDialog>
      </Container>
    );
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      {!isMobile && (
        <SidebarDrawer variant="permanent" sx={{ paddingTop: 20 }}>
          <SidebarLogo>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: "#fff",
                color: "#2ecc71",
                fontSize: 40,
                fontWeight: 700,
                boxShadow: "0 6px 20px rgba(0, 0, 0, 0.2)",
                mx: "auto",
                mb: 2,
              }}
            >
              {userData.fullName?.charAt(0).toUpperCase() || "U"}
            </Avatar>
          </SidebarLogo>
          <List>
            {navItems.map((item, index) => (
              <SidebarListItem
                button
                key={item.label}
                selected={activeTab === index}
                onClick={() => handleNavClick(index)}
                sx={{ color: 'white' }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </SidebarListItem>
            ))}
          </List>
        </SidebarDrawer>
      )}

      <MainContent>
        <Container maxWidth="md" sx={{ mt: 10, mb: 10 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            centered
            sx={{
              mb: 4,
              "& .MuiTab-root": {
                fontWeight: 600,
                textTransform: "none",
                fontSize: "1rem",
                "&.Mui-selected": {
                  color: "#2ecc71",
                },
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#2ecc71",
              },
            }}
          >
            <Tab label="Profile" />
            <Tab label="Statistics" />
            <Tab label="Subscriptions" />
          </Tabs>
          {activeTab === 0 && <ProfileSection />}
          {activeTab === 1 && <StatisticsSection />}
          {activeTab === 2 && <SubscriptionsSection />}
        </Container>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </MainContent>

      {isMobile && (
        <BottomNavigation
          showLabels
          value={activeTab}
          onChange={(event, newValue) => handleNavClick(newValue)}
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            background: "linear-gradient(180deg, #2ecc71 0%, #66bb6a 100%)",
            color: "#fff",
            boxShadow: "0 -2px 10px rgba(0, 0, 0, 0.2)",
            "& .Mui-selected": {
              color: "#fff",
              background: "rgba(255, 255, 255, 0.2)",
            },
            "& .MuiBottomNavigationAction-label": {
              fontWeight: 600,
              color: "#fff",
            },
          }}
        >
          {navItems.map((item, index) => (
            <BottomNavigationAction
              key={item.label}
              label={item.label}
              icon={item.icon}
              sx={{ color: "#fff", "&.Mui-selected": { color: "#fff" } }}
            />
          ))}
        </BottomNavigation>
      )}
    </Box>
  );
};

export default ProfileDashboard;